import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Group } from '@cobu/api-client';
import { UserInfo, Role } from '@cobu/types';

// Types for group detail data
export interface GroupMember {
    uuid: string;
    user: UserInfo;
    role: Role;
    joinedAt: string;
}

export interface GroupGathering {
    uuid: string;
    title: string;
    description?: string;
    scheduledStartTime: string;
    scheduledEndTime: string;
    location?: string;
    isAttending: boolean;
    attendeeCount: number;
    maxGuests?: number;
    buildingTimezone: string;
    locationString?: string;
}

// Define the group detail state type
export interface GroupDetailState {
    groups: Record<string, {
        group: Group;
        gatherings: GroupGathering[];
        members: GroupMember[];
        userIsGroupMember: boolean;
    }>;
    isLoading: boolean;
    error: string | null;
    isJoining: boolean;
    isLeaving: boolean;
}

// Initial state
const initialState: GroupDetailState = {
    groups: {},
    isLoading: false,
    error: null,
    isJoining: false,
    isLeaving: false,
};

// Create group detail slice with reducers
const groupDetailSlice = createSlice({
    name: 'groupDetail',
    initialState,
    reducers: {
        // Set loading state
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        // Set group data
        setGroup: (state, action: PayloadAction<{ groupId: string; group: Group }>) => {
            const { groupId, group } = action.payload;
            if (!state.groups[groupId]) {
                state.groups[groupId] = {
                    group,
                    gatherings: [],
                    members: [],
                    userIsGroupMember: group.userIsMember,
                };
            } else {
                state.groups[groupId].group = group;
                state.groups[groupId].userIsGroupMember = group.userIsMember;
            }

            state.isLoading = false;
            state.error = null;
        },

        // Set gatherings data
        setGatherings: (state, action: PayloadAction<{ groupId: string; gatherings: GroupGathering[] }>) => {
            const { groupId, gatherings } = action.payload;

            if (state.groups[groupId]) {
                state.groups[groupId].gatherings = gatherings;
            }
        },

        // Set members data
        setMembers: (state, action: PayloadAction<{ groupId: string; members: GroupMember[] }>) => {
            const { groupId, members } = action.payload;

            if (state.groups[groupId]) {
                state.groups[groupId].members = members;
            }
        },

        // Update membership status
        updateMembership: (state, action: PayloadAction<{ groupId: string; isMember: boolean }>) => {
            const { groupId, isMember } = action.payload;

            if (state.groups[groupId]) {
                state.groups[groupId].userIsGroupMember = isMember;
            }
        },

        // Set joining state
        setJoining: (state, action: PayloadAction<boolean>) => {
            state.isJoining = action.payload;
        },

        // Set leaving state
        setLeaving: (state, action: PayloadAction<boolean>) => {
            state.isLeaving = action.payload;
        },

        // Set error state
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.isLoading = false;
        },

        // Clear error state
        clearError: (state) => {
            state.error = null;
        },

        // Reset group data
        resetGroup: (state, action: PayloadAction<string>) => {
            const groupId = action.payload;
            delete state.groups[groupId];
        },

        // Reset to initial state
        reset: () => initialState,
    },
});

// Export actions and reducer
export const {
    setLoading,
    setGroup,
    setGatherings,
    setMembers,
    updateMembership,
    setJoining,
    setLeaving,
    setError,
    clearError,
    resetGroup,
    reset
} = groupDetailSlice.actions;

export default groupDetailSlice.reducer;
